import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import { ParagraphWrapper } from '../ParagraphWrapper';
import { ContentPackageHeaderSection } from '../ContentPackage/ContentPackageHeaderSection';
import { GridColumn, GridRow } from '../../responsive/atoms/Grid';
import { Container } from '../../responsive/atoms/Container';
import {
  getPrimaryTaxonomySlug,
  mapContentToCardData
} from '../../../domain/utils/contentUtils';
import { ImageSimple } from '../../Image/ImageSimple';
import { imageResizerService } from '../../../domain/services/imageResizerService';
import { theme } from '../../../domain/theme';
import { videoItemStyle } from './videoItemStyle';
import { SponsoredTag } from '../../SponsoredTag';
import { t } from '../../../domain/services/configService';
import { CardImage } from '../../Card/CardImage';
import { CardText } from '../../Card/CardText';
import { dateService } from '../../../domain/services/dateService';
import { SdkVideoPlayer } from '../../SdkVideoPlayer';
import { breakpointService } from '../../../domain/services/breakpointService';
import { isElementEntirelyInViewport } from '../../../domain/utils/domUtils';
import { SmartLink } from '../../SmartLink';

export const getStackCardDatum = data => {
  const stackCardDatum = data.videoStack.map(stackItem => {
    const cardDataFields = [
      'subtitle',
      'taxonomies',
      'videoDuration',
      'sponsored'
    ];

    const cardData = mapContentToCardData(
      stackItem.entity.videoStackItemRef.entity,
      cardDataFields
    );

    if (stackItem.entity.titleOverride) {
      cardData.title = stackItem.entity.titleOverride;
    }

    if (stackItem.entity.descriptionOverride) {
      cardData.subtitle = stackItem.entity.descriptionOverride;
    }

    if (stackItem.entity.hideDescription) {
      delete cardData.subtitle;
    }

    return cardData;
  });

  // Filter out cards without image
  return stackCardDatum.filter(stackCardData => !!stackCardData.imageEntity);
};

const getStyles = isLightBg => ({
  mediaContainer: css`
    aspect-ratio: 16 / 9;
  `,
  listItem: css`
    cursor: pointer;
    margin: 0 24px 24px 24px;
    position: relative;
    display: flex;
    height: fit-content;
    color: ${isLightBg ? 'black' : 'white'};
    ${theme.font.family('boldWeb')};
    flex-direction: row;
    gap: 24px;
    align-items: flex-start;
    ${theme.mq.tablet} {
      gap: 12px;
      margin: 0 12px 12px 0;
    }
    ${theme.mq.small_desktop} {
      gap: 24px;
      margin: 0 24px 24px 0;
    }
  `,
  listItemImage: css`
    position: relative;
    overflow: hidden;
    width: 100%;
    margin: 0;
    flex-grow: 0;
    flex-basis: 50%;
    max-width: 190px;
    align-items: center;
    aspect-ratio: 16 / 9;
    img {
      display: block;
      transition: transform 0.3s;
    }
    :hover {
      img {
        transform: scale(1.1);
      }
    }
    :before {
      background: linear-gradient(0deg, #0000, #000000b3 80%);
      content: '';
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      width: 100%;
      z-index: 1;
    }
    :after {
      content: '';
      position: absolute;
      top: 0;
      z-index: 21;
      display: block;
      width: 100%;
      height: 100%;
      background: black;
      transition: all 0.3s ease-in-out;
      opacity: 0;
    }
    :hover {
      :after {
        opacity: 0.2;
      }
    }
  `,
  listItemImageCurrent: css`
    picture:before {
      content: '';
      position: absolute;
      left: 0;
      bottom: 0;
      z-index: 2;
      display: block;
      width: 100%;
      height: 4px;
      background: #fc0;
    }
  `,
  listItemText: css`
    overflow: hidden;
    display: -webkit-box;
    flex-grow: 0;
    flex-basis: 50%;
    font-weight: unset;
    font-size: 1rem;
    ${theme.font.family('medium')};
    line-height: 1.375rem;
    letter-spacing: 0.1px;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    ${theme.mq.tablet} {
      font-size: 14px;
    }
    ${theme.mq.small_desktop} {
      font-size: 1rem;
    }
    ${theme.mq.large_desktop} {
      -webkit-line-clamp: 4;
    }
    :hover {
      text-decoration: underline;
    }
  `,
  listItemIcon: css`
    position: absolute;
    bottom: 8px;
    left: 8px;
    z-index: 1;
    background-color: rgba(0, 0, 0, 0.5);
    width: 40px;
    height: 40px;
    border-radius: 100%;
    display: grid;
    place-content: center;
    &:hover {
      background-color: #fc0;
      svg {
        color: black;
      }
    }
    svg {
      color: #fc0;
      display: block;
    }
  `,
  listItemHeader: css`
    font-size: 10px;
    letter-spacing: 2px;
    line-height: 1.4;
    ${theme.font.family('boldWeb')};
    text-align: center;
    text-transform: uppercase;
    position: absolute;
    top: 8px;
    z-index: 22;
    width: 100%;
    a:hover,
    :hover {
      text-decoration: underline;
    }
  `,
  listItemHeaderText: css`
    color: white;
    a {
      color: inherit;
    }
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    padding: 0 2px;
  `,
  listItemDuration: css`
    background-color: #00000080;
    border-radius: 4px;
    bottom: 6px;
    color: #fff;
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 3px;
    padding: 8px;
    position: absolute;
    right: 6px;
    z-index: 1;
  `,
  sponsored: css`
    div {
      margin: 0 auto;
      color: black;
      background: white;
    }
  `
});

const VideoModuleInner = ({ data: rawData, isAboveTheFold }) => {
  const data = {
    ...rawData,
    backgroundColor:
      rawData.backgroundColor === 'gray' ? '#f7f7f7' : rawData.backgroundColor
  };

  if (!data.backgroundColor) {
    data.backgroundColor = 'white';
  }

  // Filter out articles without video reference.
  data.videoStack =
    data.videoStack?.filter(item => !!item.entity?.videoStackItemRef?.entity) ||
    [];

  if (data.videoStack.length === 0) {
    return <div />;
  }

  const [currentVideoIndex, setCurrentVideoIndex] = useState(null);
  const mainVideoContainer = useRef(null);

  const stackCardDatum = getStackCardDatum(data);
  const cardStyles = videoItemStyle(data.backgroundColor);
  const hasHeaderSection = data.videoModuleTitle || data.description;

  const isLightBg = ['white', '#f7f7f7'].includes(data.backgroundColor);
  const styles = getStyles(isLightBg);

  const play = index => {
    if (currentVideoIndex !== index) {
      setCurrentVideoIndex(index);
    }
  };

  const handlePlaylistItemClick = index => {
    if (
      breakpointService.isHandheld() &&
      mainVideoContainer.current &&
      !isElementEntirelyInViewport(mainVideoContainer.current)
    ) {
      mainVideoContainer.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }

    play(index);
  };

  const onComplete = () => {
    if (data.videoStack[currentVideoIndex + 1]) {
      handlePlaylistItemClick(currentVideoIndex + 1);
    } else {
      handlePlaylistItemClick(0);
    }
  };

  // Hide title in main card, as per request.
  const cardData = stackCardDatum[currentVideoIndex || 0];
  if (data.videoStack[currentVideoIndex || 0]?.entity?.hideTitle) {
    delete cardData.title;
  }

  // Select video to be played
  let videoPlayerConfig;
  let videoArticle;
  if (currentVideoIndex !== null) {
    videoArticle =
      data.videoStack[currentVideoIndex]?.entity?.videoStackItemRef?.entity;
    const videoEntity = videoArticle?.videoRef?.entity;
    if (videoEntity) {
      const pubTimestamp =
        videoArticle.publishDate?.timestamp || videoArticle.created;
      const date = new Date(pubTimestamp * 1000);

      videoPlayerConfig = {
        autoplay: true,
        related_videos_plugin: false,
        id: videoEntity.guid,
        video_category: videoArticle.primaryTaxonomy?.entity?.name,
        slug: getPrimaryTaxonomySlug(videoArticle),
        ptt_id: videoArticle.primaryTaxonomy?.entity?.tid,
        channel: videoEntity.show?.channel,
        pg_rating: videoEntity.show?.rating,
        showTitle: videoEntity.show?.title,
        video_publicationDate: dateService.format(date, 'd/m/Y')
      };

      if (videoArticle.noAds || videoEntity.noAds) {
        videoPlayerConfig.ads = 'false';
      }
    }
  }

  return (
    <Container
      margin="0 auto"
      padding={['0 0 56px 0', '0 0 56px 0', '0 0 56px 0', '0 0 80px 0']}
      backgroundColor={data.backgroundColor}
    >
      {hasHeaderSection && (
        <Container
          maxWidth="100rem"
          padding={['0 0 0 0', '0 0 0 0', '0 0 0 0', '0 56px']}
        >
          <GridRow gutter="none">
            <GridColumn width={12}>
              <ContentPackageHeaderSection
                title={data.videoModuleTitle}
                description={data.description}
              />
            </GridColumn>
          </GridRow>
        </Container>
      )}

      <Container
        maxWidth="100rem"
        padding={
          hasHeaderSection
            ? null
            : ['0 0 0 0', '56px 0 0 0', '56px 0 0 0', '80px 0 0 0']
        }
      >
        <GridRow gutter={[null, '12px', '20px', '20px']}>
          <GridColumn width={[12, 7, 8, 8]} childrenMarginBottom="56px">
            <section ref={mainVideoContainer} css={cardStyles.container}>
              <div css={styles.mediaContainer}>
                {videoPlayerConfig && (
                  <SdkVideoPlayer
                    config={videoPlayerConfig}
                    content={videoArticle}
                    onComplete={onComplete}
                  />
                )}

                {!videoPlayerConfig && cardData.imageEntity ? (
                  <CardImage
                    type={cardData.type}
                    url={cardData.url}
                    nativeLink
                    onClick={e => {
                      e.preventDefault();
                      play(currentVideoIndex || 0);
                    }}
                    target={cardData.target}
                    title={cardData.title}
                    imageEntity={cardData.imageEntity}
                    imageMaxWidths={{
                      mobile: 727,
                      tablet: 559,
                      small_desktop: 786,
                      large_desktop: 973
                    }}
                    aspect="16x9"
                    isAboveTheFold={isAboveTheFold}
                    styles={cardStyles.image}
                  />
                ) : null}
              </div>

              <CardText
                type={cardData.type}
                url={cardData.url}
                nativeLink
                onClick={e => {
                  e.preventDefault();
                  play(currentVideoIndex || 0);
                }}
                target={cardData.target}
                title={cardData.title}
                subtitle={cardData.subtitle}
                videoDuration={cardData.videoDuration}
                sponsored={cardData.sponsored}
                taxonomies={cardData.taxonomies}
                showCTA={cardData.showCTA}
                styles={cardStyles.text}
              />
            </section>
          </GridColumn>

          <GridColumn width={[12, 5, 4, 4]} childrenMarginBottom="24px">
            {stackCardDatum.map((stackCardData, index) => {
              let headerItem;
              if (currentVideoIndex === index) {
                headerItem = (
                  <span css={styles.listItemHeaderText}>
                    {t('Now Playing')}
                  </span>
                );
              } else {
                const ptt =
                  stackCardData.taxonomies &&
                  stackCardData.taxonomies.find(term => term.isPtt);

                if (stackCardData.sponsored) {
                  headerItem = (
                    <span css={styles.sponsored}>
                      <SponsoredTag text={stackCardData.sponsored.text} />
                    </span>
                  );
                } else if (ptt && ptt.name) {
                  headerItem = (
                    <span css={styles.listItemHeaderText}>
                      <SmartLink to={ptt.url}>{ptt.name}</SmartLink>
                    </span>
                  );
                }
              }

              return (
                <div
                  css={styles.listItem}
                  key={stackCardData.imageEntity.mediaImage.url}
                  onClick={() => handlePlaylistItemClick(index)}
                  onKeyDown={() => handlePlaylistItemClick(index)}
                  role="button"
                  aria-label={t('View')}
                  tabIndex="0"
                >
                  <div
                    css={[
                      styles.listItemImage,
                      currentVideoIndex === index
                        ? styles.listItemImageCurrent
                        : null
                    ]}
                  >
                    <div css={styles.listItemHeader}>{headerItem}</div>
                    <ImageSimple
                      src={imageResizerService.getStyleUrl(
                        stackCardData.imageEntity.mediaImage.url,
                        190
                      )}
                      alt={stackCardData.title}
                    />

                    <div css={styles.listItemDuration}>
                      {dateService.secondsToMinutes(
                        stackCardData.videoDuration
                      )}
                    </div>
                  </div>
                  <h2 css={styles.listItemText}>{stackCardData.title}</h2>
                </div>
              );
            })}
          </GridColumn>
        </GridRow>
      </Container>
    </Container>
  );
};

VideoModuleInner.defaultProps = {
  isAboveTheFold: false
};

VideoModuleInner.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  isAboveTheFold: PropTypes.bool
};

export const VideoModule = ParagraphWrapper(VideoModuleInner);
